import { skipToken } from '@apollo/client/react'
import { cloneElement, useCallback } from 'react'
import { toast } from 'react-toastify'
import FluentCheckmark16Filled from '~icons/fluent/checkmark-16-filled'
import FluentLinkMultiple16Filled from '~icons/fluent/link-multiple-16-filled'
import IcBaselineIosShare from '~icons/ic/baseline-ios-share'
import {
  getArtworkShareLink,
  getPlatformsByLocale,
  sharePlatformMap,
} from 'src/components/SocialShare/SocialShare'
import { useCopy } from 'src/hooks/useCopy'
import { useGetArtworkWithTaskDetail } from 'src/services/apollo/artwork'
import { useMyInfo } from 'src/services/apollo/auth'
import type { Artwork } from 'src/services/apollo/generated/graphql'
import { useGetGenerationModelByVersionIdForArtwork } from 'src/services/apollo/generation-model'
import {
  executeClaimShareCredit,
  useGetMyShareCreditInfo,
} from 'src/services/apollo/quota'
import { getLocale } from 'src/stores/intl'
import { cx } from 'src/utils/cx'
import { translateErrorCode } from 'src/utils/errorCodeTranslator'
import { getErrorCode } from 'src/utils/errorUtil'
import { getPublicUrl } from 'src/utils/media'
import { isMMDIT26AModel } from 'src/utils/model'
import { formatParamURL } from 'src/utils/qs'
import { track } from 'src/utils/tracker'
import { isMobileDevice } from 'src/utils/ua'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import styles from './index.module.css'
import { normalizeTackNameForShare } from './utils'

function getShareText(artwork: Artwork, isTsubaki2: boolean) {
  const brandSuffix = isTsubaki2 ? '#PixAI #PixAITsubaki2' : '#PixAI'
  if (artwork.title) return `${artwork.title} ${brandSuffix}`
  // No title: use first tack as context, or a minimal fallback
  const firstTack = artwork.tacks?.[0]?.defaultName
  return firstTack
    ? `#${firstTack} on ${brandSuffix}`
    : `Made with ${brandSuffix}`
}

/** Read model type from Apollo cache only (no network request) */
function useIsTsubaki2(artworkId: string) {
  const { data: artworkData } = useGetArtworkWithTaskDetail(
    { id: artworkId },
    {
      fetchPolicy: 'cache-only',
    },
  )
  type GeneratorTaskParameters = {
    modelId?: string
  }
  const generatorParams = artworkData?.taskParameters?.parameters as
    | GeneratorTaskParameters
    | undefined
  const modelVersionId = generatorParams?.modelId
  const { data: modelVersion } = useGetGenerationModelByVersionIdForArtwork(
    modelVersionId ?? skipToken,
    { fetchPolicy: 'cache-only' },
  )
  return isMMDIT26AModel(modelVersion?.model?.type)
}

export default function ArtworkSocialShareButtons(props: {
  artwork: Artwork
  scene?: string
}) {
  const [copy, copied] = useCopy()
  const { artwork, scene } = props
  const artworkImageUrl = getPublicUrl(artwork.media)
  const isTsubaki2 = useIsTsubaki2(artwork.id)

  const { data: userInfo } = useMyInfo()
  const { data: { countRemains } = {} } = useGetMyShareCreditInfo(
    userInfo?.id ? {} : skipToken,
  )

  const [sharing, onSharing] = useLoadingCallback(
    async (platform?: string) => {
      if (!userInfo?.id) return
      if ((countRemains ?? 0) < 1) return // No more credits to claim

      try {
        await executeClaimShareCredit({})
        track('share_credits_dialog_show', {
          fromPath: platform,
          scene,
        })
      } catch (e: any) {
        toast.error(
          translateErrorCode(getErrorCode(e), { fallback: e.message }),
        )
      }
    },
    [userInfo?.id, countRemains],
  )

  const onShareClick = useCallback(
    (platform: string) => {
      if (sharing) return

      track('share_platform_btn_click', {
        value: platform,
        scene,
      })
      return onSharing(platform)
    },
    [sharing, onSharing, scene],
  )

  const sharePlatforms = getPlatformsByLocale(getLocale())

  const shareProps = useCallback(
    (platform: keyof typeof sharePlatformMap) => {
      const data = sharePlatformMap[platform]
      const shareLink = formatParamURL(getArtworkShareLink(artwork.id), {
        utm_medium: 'referral',
        utm_source: platform,
        utm_content: 'share',
      })
      const artworkTags =
        artwork.tacks
          ?.map(t => t.displayName ?? t.defaultName)
          .map(name => normalizeTackNameForShare(name))
          .filter(Boolean) ?? []
      const href = data.shareUrl({
        link: shareLink,
        image: artworkImageUrl,
        text: getShareText(artwork, isTsubaki2),
        tags: artworkTags,
      })

      return {
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
          onShareClick(platform)
          if ('shouldCopy' in data && data.shouldCopy) {
            e.preventDefault()
            e.stopPropagation()
            window.open(href, '_blank', 'noopener,noreferrer')
            copy(shareLink, { scene })
          }
        },
      }
    },
    [artwork, artworkImageUrl, isTsubaki2, onShareClick, scene, copy],
  )

  const supportsNativeShare =
    typeof navigator !== 'undefined' && !!navigator.share && isMobileDevice()

  const onNativeShare = useCallback(async () => {
    const shareLink = formatParamURL(getArtworkShareLink(artwork.id), {
      utm_medium: 'referral',
      utm_source: 'native_share',
      utm_content: 'share',
    })
    const shareText = getShareText(artwork, isTsubaki2)

    try {
      await navigator.share({
        text: shareText,
        url: shareLink,
      })
      track('share_platform_btn_click', {
        value: 'native_share',
        scene,
      })
      onSharing('native_share')
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      console.error('Native share failed:', err)
    }
  }, [artwork, isTsubaki2, scene, onSharing])

  return (
    <>
      {supportsNativeShare && (
        <button
          type="button"
          className={styles['rounded-btn']}
          onClick={onNativeShare}
          disabled={sharing}
          aria-label="Share"
        >
          <IcBaselineIosShare className="text-xl" />
        </button>
      )}
      {sharePlatforms.map(platform => (
        <a
          key={platform}
          className={cx('inline-grid place-items-center size-12 rounded-full')}
          style={{
            backgroundColor: sharePlatformMap[platform].color,
            color:
              'textColor' in sharePlatformMap[platform] &&
              typeof sharePlatformMap[platform].textColor === 'string'
                ? sharePlatformMap[platform].textColor
                : '#fff',
            ...('borderColor' in sharePlatformMap[platform] &&
            typeof sharePlatformMap[platform].borderColor === 'string'
              ? {
                  border: `1px solid ${sharePlatformMap[platform].borderColor}`,
                }
              : {}),
          }}
          {...shareProps(platform)}
        >
          {cloneElement(sharePlatformMap[platform].icon, {
            className: 'size-7',
          })}
        </a>
      ))}
      <button
        className={styles['rounded-btn']}
        onClick={e => {
          track('copy_link_click', {
            scene,
            location: 'share',
          })
          copy(
            formatParamURL(getArtworkShareLink(artwork.id), {
              utm_source: 'copy_web',
            }),
          )
        }}
        disabled={sharing}
      >
        {copied ? (
          <FluentCheckmark16Filled className="text-xl text-green-500" />
        ) : (
          <FluentLinkMultiple16Filled className="text-xl" />
        )}
      </button>
    </>
  )
}
